import { useLayoutEffect, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronDown } from "lucide-react"

import type { VoteOptimizationStrategyType } from "@/domains/vote-optimizer/vote-optimization-strategy-factory"
import { SearchIcon } from "@/domains/icons"
import { CoinsIcon } from "@/domains/icons/huge-icons/monotone/coins"
import { FlameIcon } from "@/domains/icons/huge-icons/monotone/flame"
import { ShieldIcon } from "@/domains/icons/huge-icons/monotone/shield"
import { SwordIcon } from "@/domains/icons/huge-icons/monotone/sword"
import { cn } from "@/domains/library"
import { STRATEGY_ICON } from "@/domains/vote-optimizer/vote-optimization-strategy-factory"
import { TokenIcon } from "@/domains/tokens/components/token-icon"

export interface OptimizationStrategyOption {
  id: VoteOptimizationStrategyType
  label: string
  estimatedValue: number
  icon: string
}

export interface TokenStrategyOption {
  address: string
  symbol: string
  name: string
  imageURL: string
  displayValue: string
}

export interface OptimizationEngineDropdownViewProps {
  strategies: OptimizationStrategyOption[]
  featuredTokens: TokenStrategyOption[]
  searchableTokens: TokenStrategyOption[]
  searchQuery: string
  onSearchChange: (value: string) => void
  selectedEngineType: "optimization" | "token" | null
  selectedOptimizationStrategy: VoteOptimizationStrategyType | null
  selectedTokenAddress: string | null
  onSelectOptimizationStrategy: (id: VoteOptimizationStrategyType) => void
  onSelectTokenStrategy: (address: string) => void
  isOpen: boolean
  onToggle: () => void
  onClickOutside: () => void
  triggerRef: React.RefObject<HTMLDivElement | null>
  dropdownRef: React.RefObject<HTMLDivElement | null>
}

function getStrategyIconComponent(icon: string) {
  switch (icon) {
    case STRATEGY_ICON.SHIELD:
      return ShieldIcon
    case STRATEGY_ICON.FLAME:
      return FlameIcon
    case STRATEGY_ICON.SWORD:
      return SwordIcon
    case STRATEGY_ICON.TOKEN:
      return CoinsIcon
    default:
      return ShieldIcon
  }
}

export function OptimizationEngineDropdownView({
  strategies,
  featuredTokens,
  searchableTokens,
  searchQuery,
  onSearchChange,
  selectedEngineType,
  selectedOptimizationStrategy,
  selectedTokenAddress,
  onSelectOptimizationStrategy,
  onSelectTokenStrategy,
  isOpen,
  onToggle,
  onClickOutside,
  triggerRef,
  dropdownRef,
}: OptimizationEngineDropdownViewProps) {
  const selectedStrategy = strategies.find((s) => s.id === selectedOptimizationStrategy)
  const selectedToken = [...featuredTokens, ...searchableTokens].find((t) => t.address === selectedTokenAddress)

  const displayLabel =
    selectedEngineType === "optimization" && selectedStrategy
      ? selectedStrategy.label
      : selectedEngineType === "token" && selectedToken
        ? selectedToken.name
        : "Select strategy"

  const iconBoxClass =
    selectedEngineType === "optimization"
      ? "bg-blue-500/20 text-blue-400"
      : "bg-gray-800/50 text-gray-500"

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const dropdownWidth = 320
      setDropdownPosition({
        top: rect.bottom + 12,
        left: rect.right - dropdownWidth,
      })
    }
  }, [isOpen])

  const dropdownContent = isOpen && (
    <>
      <div
        className="fixed inset-0 z-40"
        aria-hidden="true"
        onClick={onClickOutside}
        onKeyDown={(e) => e.key === "Escape" && onClickOutside()}
      />
      <div
        ref={dropdownRef}
        className="fixed z-50 w-80 overflow-hidden rounded-2xl border border-gray-700 bg-[#1a1a1a] shadow-[0_20px_60px_rgba(0,0,0,0.8)]"
        style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
      >
        <div className="max-h-[440px] overflow-y-auto">
          <div className="border-b border-gray-800/50 bg-[#1d1d1d]/30 px-4 py-3">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">
              Optimisation Strategies
            </p>
            <div className="space-y-1">
              {strategies.map((strategy) => {
                const Icon = getStrategyIconComponent(strategy.icon)
                const isSelected =
                  selectedEngineType === "optimization" && selectedOptimizationStrategy === strategy.id

                return (
                  <button
                    key={strategy.id}
                    type="button"
                    onClick={() => {
                      onSelectOptimizationStrategy(strategy.id)
                      onClickOutside()
                    }}
                    className={cn(
                      "flex w-full items-center justify-between rounded-xl p-3 transition-all",
                      isSelected ? "bg-blue-500/10" : "hover:bg-gray-800 group",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
                          isSelected ? "bg-blue-500 text-black" : "bg-blue-500/20 text-blue-400",
                        )}
                      >
                        <Icon size={16} />
                      </div>
                      <div className="text-left">
                        <p className={cn("text-xs font-bold transition-colors", isSelected ? "text-white" : "text-gray-400 group-hover:text-white")}>
                          {strategy.label}
                        </p>
                        <p className="text-[9px] font-medium text-gray-600">Optimized</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-mono font-bold text-white">${strategy.estimatedValue.toFixed(2)}</p>
                      {isSelected && (
                        <span className="mt-1 block text-right text-blue-500" aria-hidden="true">
                          ✓
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="px-4 py-3">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">Token Strategy</p>
            <div className="relative mb-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search symbols (e.g. WETH)..."
                className="w-full rounded-lg border border-gray-800 bg-black py-2.5 pl-9 pr-3 text-xs text-white outline-none transition-all placeholder:text-gray-600 focus:border-orange-500"
              />
              <SearchIcon
                size={14}
                className="absolute left-3 top-2.5 text-gray-600"
              />
            </div>

            <div className="space-y-1">
              {featuredTokens.length > 0 || searchableTokens.length > 0 ? (
                [...featuredTokens, ...searchableTokens].map((token) => {
                  const isSelected = selectedEngineType === "token" && selectedTokenAddress === token.address

                  return (
                    <button
                      key={token.address}
                      type="button"
                      onClick={() => {
                        onSelectTokenStrategy(token.address)
                        onClickOutside()
                      }}
                      className={cn(
                        "flex w-full items-center justify-between rounded-xl p-3 transition-all",
                        isSelected ? "bg-orange-500/10" : "hover:bg-gray-800 group",
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden">
                          <TokenIcon token={{ symbol: token.symbol, imageURL: token.imageURL }} size={32} />
                        </div>
                        <div className="text-left">
                          <p className={cn("text-xs font-bold transition-colors", isSelected ? "text-white" : "text-gray-400 group-hover:text-white")}>
                            {token.name}
                          </p>
                          <p className="text-[9px] font-medium text-gray-600">Incentives Revenue</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-mono font-bold text-white">
                          {token.displayValue} <span className="text-[10px] text-gray-500">{token.symbol}</span>
                        </p>
                        {isSelected && (
                          <span className="mt-1 block text-right text-orange-500" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </div>
                    </button>
                  )
                })
              ) : (
                <p className="py-4 text-center text-[10px] italic text-gray-600">No matching symbols found.</p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 bg-black/40 px-3 py-2 text-center text-[9px] font-medium text-gray-600">
          Values updated in real-time based on your locked NFTs.
        </div>
      </div>
    </>
  )

  return (
    <div className="relative" ref={triggerRef}>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex w-full min-w-[200px] max-w-[288px] items-center justify-between gap-4 rounded-xl border-2 px-5 py-4 transition-all",
          isOpen ? "border-orange-500 bg-[#1a1a1a]" : "border-gray-800 bg-[#141414] hover:border-gray-700",
        )}
      >
        <div className="flex items-center gap-3">
          {selectedEngineType === "token" && selectedToken ? (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden">
              <TokenIcon token={{ symbol: selectedToken.symbol, imageURL: selectedToken.imageURL }} size={32} />
            </div>
          ) : (
            <div className={cn("flex h-8 w-8 items-center justify-center rounded-lg", iconBoxClass)}>
              {selectedEngineType === "optimization" && selectedStrategy ? (
                (() => {
                  const Icon = getStrategyIconComponent(selectedStrategy.icon)
                  return <Icon size={16} />
                })()
              ) : (
                <ShieldIcon size={16} className="text-gray-500" />
              )}
            </div>
          )}
          <div className="text-left">
            <p className="text-[9px] font-bold uppercase leading-none tracking-tighter text-gray-500">
              Optimization Engine
            </p>
            <p className="text-sm font-bold text-white">{displayLabel}</p>
          </div>
        </div>
        <ChevronDown
          size={18}
          className={cn("text-gray-500 transition-transform duration-300", isOpen && "rotate-180")}
        />
      </button>

      {isOpen && createPortal(dropdownContent, document.body)}
    </div>
  )
}
